// ============================================================
// Bulk Import Student Modal
// ============================================================

import { useState, useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, IconButton, CircularProgress, Alert, AlertTitle, Paper, Divider } from '@mui/material';
import { Upload, Download, FileSpreadsheet, X, Trash2, Info, CheckCircle2, FileCheck2, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { tokens } from '@/theme/tokens';
import { useSchool } from '@/hooks/useSchool';
import studentService from '@/services/studentService';

export function BulkImportStudentModal({ open, onClose, onSuccess }) {
  const { schoolId } = useSchool();
  const fileInputRef = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [importing, setImporting] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [resultMessage, setResultMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [errorDetails, setErrorDetails] = useState([]);

  const resetState = () => {
    setSelectedFile(null);
    setErrorMessage(null);
    setErrorDetails([]);
    setResultMessage(null);
    setIsDragOver(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    if (importing) return;
    resetState();
    onClose();
  };

  // ── Download Template ──────────────────────────────────────
  const handleDownloadTemplate = async () => {
    if (!schoolId) {
      toast.error('School context is missing.');
      return;
    }

    setDownloadingTemplate(true);
    try {
      const response = await studentService.downloadImportTemplate(schoolId);

      // Determine blob and filename
      const blob = response?.data instanceof Blob ? response.data : (response instanceof Blob ? response : new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));

      let filename = 'student_import_template.xlsx';
      const contentDisposition = response?.headers?.['content-disposition'] || response?.headers?.['Content-Disposition'];
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match && match[1]) {
          filename = match[1].replace(/['"]/g, '').trim();
        }
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Template downloaded successfully!');
    } catch (err) {
      console.error('Failed to download import template:', err);
      toast.error(err?.response?.data?.message || err?.message || 'Failed to download template. Please try again.');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  // ── File Selection & Drag-and-Drop ─────────────────────────
  const validateAndSetFile = (file) => {
    setErrorMessage(null);
    setErrorDetails([]);
    setResultMessage(null);

    if (!file) return;

    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const fileNameLower = file.name.toLowerCase();
    const isValid = validExtensions.some(ext => fileNameLower.endsWith(ext));

    if (!isValid) {
      setErrorMessage('Please upload a valid Excel or CSV spreadsheet file (.xlsx, .xls, .csv).');
      return;
    }

    // Limit to 15MB
    if (file.size > 15 * 1024 * 1024) {
      setErrorMessage('File size exceeds the 15MB limit. Please upload a smaller file.');
      return;
    }

    setSelectedFile(file);
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      validateAndSetFile(e.target.files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  const handleRemoveFile = (e) => {
    e.stopPropagation();
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  // ── Submit Import ──────────────────────────────────────────
  const handleImportSubmit = async () => {
    if (!schoolId) {
      toast.error('School context is missing.');
      return;
    }
    if (!selectedFile) {
      setErrorMessage('Please select or drop an Excel spreadsheet file to import.');
      return;
    }

    setImporting(true);
    setErrorMessage(null);
    setErrorDetails([]);
    setResultMessage(null);

    try {
      const response = await studentService.importStudents(schoolId, selectedFile);
      const data = response?.data || response;
      const successMsg = response?.message || data?.message || 'Students imported successfully!';

      toast.success(successMsg);
      setResultMessage(successMsg);

      if (onSuccess) {
        onSuccess();
      }

      // Close modal after a brief delay to let user see confirmation
      setTimeout(() => {
        handleClose();
      }, 1400);
    } catch (err) {
      console.error('Import students error:', err);
      const resData = err?.response?.data;
      let msg = resData?.detail || resData?.message || err?.message || 'Failed to import students. Please verify your file.';

      let detailsList = [];
      if (Array.isArray(resData?.detail)) {
        detailsList = resData.detail.map(d => typeof d === 'string' ? d : (d.msg || d.message || JSON.stringify(d)));
        msg = 'Validation errors were encountered while processing the spreadsheet:';
      } else if (Array.isArray(resData?.errors)) {
        detailsList = resData.errors.map(d => typeof d === 'string' ? d : (d.msg || d.message || JSON.stringify(d)));
      } else if (typeof resData?.detail === 'string') {
        msg = resData.detail;
      }

      setErrorMessage(msg);
      setErrorDetails(detailsList);
      toast.error(typeof msg === 'string' && msg.length < 100 ? msg : 'Import failed. Please review errors.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 1,
          boxShadow: '0 20px 48px rgba(0,0,0,0.14)',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, pt: 1.5, px: 2.5, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FileSpreadsheet size={22} color={tokens.colors.primary.main} />
            Bulk Import Students
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Enroll multiple students in one go using the official Excel template.
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} disabled={importing} sx={{ color: 'text.secondary' }}>
          <X size={18} />
        </IconButton>
      </DialogTitle>

      <Divider sx={{ my: 1 }} />

      <DialogContent sx={{ py: 2, px: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* Step 1: Download Template Card */}
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor: `${tokens.colors.primary.main}06`,
            borderColor: `${tokens.colors.primary.main}25`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" fontWeight={700} color="primary.main" gutterBottom>
                Step 1: Download Standard Template
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.5 }}>
                Download the predefined Excel spreadsheet containing the exact columns, headers, and format required for admission.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={downloadingTemplate ? <CircularProgress size={14} color="inherit" /> : <Download size={15} />}
              onClick={handleDownloadTemplate}
              disabled={downloadingTemplate || importing}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 2,
                py: 0.75,
                whiteSpace: 'nowrap',
                backgroundColor: '#ffffff',
              }}
            >
              {downloadingTemplate ? 'Downloading...' : 'Download Template (.xlsx)'}
            </Button>
          </Box>
        </Paper>

        {/* Step 2: Upload File Card */}
        <Box>
          <Typography variant="subtitle2" fontWeight={700} color="text.primary" sx={{ mb: 1 }}>
            Step 2: Upload Completed Spreadsheet
          </Typography>

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".xlsx, .xls, .csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, text/csv"
            style={{ display: 'none' }}
          />

          {!selectedFile ? (
            <Box
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                border: '2px dashed',
                borderColor: isDragOver ? tokens.colors.primary.main : `${tokens.colors.primary.main}40`,
                backgroundColor: isDragOver ? `${tokens.colors.primary.main}12` : `${tokens.colors.primary.main}04`,
                borderRadius: 2.5,
                p: 3.5,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: tokens.colors.primary.main,
                  backgroundColor: `${tokens.colors.primary.main}08`,
                },
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  backgroundColor: `${tokens.colors.primary.main}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 1.5,
                }}
              >
                <Upload size={24} color={tokens.colors.primary.main} />
              </Box>
              <Typography variant="body2" fontWeight={600} color="text.primary">
                Click to browse or drag & drop your Excel file here
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Supported formats: .xlsx, .xls, .csv (Max 15MB)
              </Typography>
            </Box>
          ) : (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderColor: `${tokens.colors.primary.main}40`,
                backgroundColor: `${tokens.colors.primary.main}06`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, overflow: 'hidden' }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    backgroundColor: '#107c4115',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <FileSpreadsheet size={24} color="#107c41" />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} color="text.primary" noWrap>
                    {selectedFile.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(selectedFile.size)} • Ready for import
                  </Typography>
                </Box>
              </Box>

              <IconButton
                size="small"
                onClick={handleRemoveFile}
                disabled={importing}
                sx={{ color: 'error.main', '&:hover': { backgroundColor: 'error.50' } }}
              >
                <Trash2 size={18} />
              </IconButton>
            </Paper>
          )}
        </Box>

        {/* Feedback / Results / Errors */}
        {resultMessage && (
          <Alert severity="success" icon={<CheckCircle2 size={20} />} sx={{ borderRadius: 2 }}>
            {resultMessage}
          </Alert>
        )}

        {errorMessage && (
          <Alert severity="error" icon={<AlertCircle size={20} />} sx={{ borderRadius: 2 }}>
            <AlertTitle sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Import Failed</AlertTitle>
            <Typography variant="body2">{errorMessage}</Typography>
            {errorDetails.length > 0 && (
              <Box component="ul" sx={{ mt: 1, pl: 2, mb: 0, fontSize: '0.8125rem' }}>
                {errorDetails.slice(0, 8).map((detail, idx) => (
                  <li key={idx}>{detail}</li>
                ))}
                {errorDetails.length > 8 && (
                  <li>...and {errorDetails.length - 8} more error(s)</li>
                )}
              </Box>
            )}
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', px: 0.5 }}>
          <Info size={16} style={{ flexShrink: 0 }} />
          <Typography variant="caption" sx={{ lineHeight: 1.4 }}>
            Ensure all mandatory fields such as First Name, Last Name, Class, and Parent information are filled in correctly.
          </Typography>
        </Box>
      </DialogContent>

      <Divider sx={{ my: 0.5 }} />

      <DialogActions sx={{ p: 2, px: 2.5, gap: 1.5 }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={handleClose}
          disabled={importing}
          sx={{ borderRadius: 2, textTransform: 'none', px: 2.5 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleImportSubmit}
          disabled={!selectedFile || importing}
          startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <FileCheck2 size={18} />}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            py: 0.8,
            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.25)',
          }}
        >
          {importing ? 'Importing Students...' : 'Upload & Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
